import { ChronoUnit } from "@js-joda/core";
import { useGetDataInfoRange, useGetDatasetInfo } from "api/api-hooks/datasets-hooks";
import { DatasetDataApi } from "api/DatasetDataApi";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { DatasetInfo } from "api/model/dataset/DatasetInfo";
import { UpdateDatasetData } from "api/model/requests";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { ICombinedData } from "components/dashboard/data-input-output-table/common/api/interfaces/interfaces";
import {
  getExtractedDataValue,
  remakeArrayIntoOtherInterface,
} from "components/dashboard/data-input-output-table/utils";
import {
  calculateStepTime,
  DatasetTypes,
  formatDateBasedOnDataType,
} from "components/dashboard/dataset-window/utils/utils";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { TitleOverrideAndDescriptionTemplate } from "components/templates/DataTableCellTemplates";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import React, { useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";
import { addToDate, ChronoUnitLegacy } from "utils/time-utils";

type AddDataDialogProps = {
  selectedDatasetID: any;
  firstLoadDate: Date;
  dateFrequency: ChronoUnitLegacy;
  closeDialog(): void;
};

interface RowData {
  key: string;
  value: number;
}

export const WARNING_LIMIT = 100;

const AddDataDialog = ({
  selectedDatasetID,
  firstLoadDate,
  dateFrequency,
  closeDialog,
}: AddDataDialogProps) => {
  const identifiers = selectedDatasetID.map((ds: DatasetInfo) => ds.identifier);
  const queryData = useGetDatasetInfo(identifiers);

  const datasetType: DatasetTypes =
    queryData && queryData.isSuccess
      ? (queryData.data!.dataType as unknown as DatasetTypes)
      : "HOURLY";

  const [dataQuery, setDataQuery] = useState<Array<any>>([]);

  const [originalDataQuery, setOriginalDataQuery] = useState<Array<ICombinedData>>([]);
  const [rangeSelected, setRangeSelected] = useState(false);
  const [selectedTime, setSelectedTime] = useState<ChronoUnit>(ChronoUnit.MINUTES);
  const [timeValue, setTimeValue] = useState(0);
  const [dateFrom, setDateFrom] = useState<Date>(addToDate(new Date(), -6, "HOURS"));
  const [dateTo, setDateTo] = useState<Date>(new Date());
  const [valueCount, setValueCount] = useState<number>(0);
  const [isValuesUpdated, setIsValuesUpdated] = useState<boolean>(false);

  const fetchQuery = useGetDataInfoRange(
    identifiers,
    {
      timeStart: rangeSelected
        ? dateFrom.toISOString()
        : dateTo.toISOString() || firstLoadDate.toISOString(),
      timeEnd: rangeSelected ? dateTo.toISOString() : undefined,
    },
    {
      enabled: dateFrom && dateTo && identifiers.length > 0,
    }
  );

  useEffect(() => {
    if (datasetType) {
      setDateFrom(addToDate(formatDateBasedOnDataType(datasetType), -6, "HOURS"));
      setDateTo(formatDateBasedOnDataType(datasetType));
    }
  }, [datasetType]);

  useEffect(() => {
    const from = sessionStorage.getItem("dateFrom");
    const to = sessionStorage.getItem("dateTo");
    if (from) setDateFrom(new Date(from));
    if (to) setDateTo(new Date(to));
  }, []);

  const titleColumnRender = (rowData: ICombinedData) => {
    return (
      <div className="flex gap-2">
        <TitleOverrideAndDescriptionTemplate
          data={{
            title: rowData.title,
            overriddenTitle: rowData.overriddenTitle,
            description: rowData.description,
            identifier: rowData.identifier,
          }}
        />
        <ChartInDialogButton identifier={rowData.identifier} />
      </div>
    );
  };

  useEffect(() => {
    if (
      fetchQuery.data &&
      !fetchQuery.isError &&
      !fetchQuery.isLoading &&
      fetchQuery.data.length > 0
    ) {
      const newData = remakeArrayIntoOtherInterface(fetchQuery.data, selectedDatasetID);
      setDataQuery([...newData]);
      setOriginalDataQuery([...newData]);
    }
  }, [fetchQuery.data]);

  useEffect(() => {
    fetchQuery.refetch();
  }, [dateTo, dateFrom]);

  const updateHandler = () => {
    if (dataQuery[0].data.length > 0) {
      if (valueCount < WARNING_LIMIT) {
        updateData();
        return;
      }
      easasConfirmDialog({
        icon: "pi pi-exclamation-triangle",
        header: T("confirm-dialog.confirmation"),
        message: T("confirm-dialog.do-you-want-to-add-points", { valueCount }),
        confirmedCallback: updateData,
      });
    } else {
      easasAlert({
        summary: T("alert.warn"),
        detail: T("alert.no-changes-made"),
        severity: "warn",
      });
    }
  };

  const updateData = () => {
    const tempArr: Array<UpdateDatasetData | undefined> = dataQuery
      .map(rowData => {
        const tempOb = fetchQuery.data?.find((ob: DatasetDataInfoResponse) => ob.identifier === rowData.identifier);
        if (!tempOb || rowData.data.length === 0) return undefined;

        if (tempOb.data.length === 0 && !rowData.data[0].value && rowData.data[0].value !== 0) {
          return undefined;
        }

        const filteredValues = rowData.data.filter((row: RowData) => {
          const existingEntry = tempOb.data.find(ob => ob.key === row.key);
          return !existingEntry || existingEntry.value !== row.value;
        });

        if (filteredValues.length === 0) {
          return undefined;
        }

        const objVal: Record<string, any> = {};

        if (rangeSelected) {
          rowData.data.forEach(({ key, value }: RowData) => {
            objVal[key] = value;
          });
        } else {
          const dateString = rowData.data.length > 0 ? rowData.data[0].key : dateTo.toISOString();
          objVal[dateString] = rowData.data[0].value;
        }

        return {
          identifier: rowData.identifier,
          values: objVal,
        };
      })
      .filter(Boolean) as Array<UpdateDatasetData>;

    if (tempArr.length === 0) {
      easasAlert({
        summary: T("alert.warn"),
        detail: T("alert.no-changes-made"),
        severity: "warn",
      });
      return;
    }

    DatasetDataApi.updateData(tempArr as Array<UpdateDatasetData>)
      .then(() => {
        easasAlert({
          summary: T("alert.success"),
          detail: T("alert.datasets-updated"),
          severity: "success",
        });
        setIsValuesUpdated(false);
      })
      .catch(er => {
        console.error(er);
        easasAlert({
          summary: T("alert.error"),
          detail: T("alert.error-while-updating-datasets"),
          severity: "error",
        });
        closeDialog();
      });
  };

  const valueBodyInputTemplate = (rowData: ICombinedData) => {
    let suffix = "";

    if (rowData.unit.length > 0 && rowData.unit !== " ") {
      suffix = ` ${rowData.unit}`;
    }

    return (
      <InputNumber
        value={!rangeSelected && getExtractedDataValue(rowData.data, rowData.decimalPlaces)}
        onValueChange={e => valueChangeHandler(rowData.identifier as string, e.value!)}
        mode="decimal"
        minFractionDigits={rowData.decimalPlaces}
        maxFractionDigits={rowData.decimalPlaces}
        suffix={suffix}
        disabled={rangeSelected && timeValue === 0}
        tooltip={T("tooltip.first-select-a-time-value")}
        tooltipOptions={{ position: "bottom", disabled: !rangeSelected || timeValue !== 0 }}
        placeholder={suffix}
      />
    );
  };

  const valueChangeHandler = (identifier: string, newValue: number) => {
    let hasChanged = false;

    const tempArr = dataQuery.map(item => {
      if (item.identifier === identifier) {
        if (rangeSelected) {
          const dateFromForMonths = new Date(dateFrom.getTime());
          let calculateFrom = dateFrom.getTime();
          const calculateTo = dateTo.getTime();

          if (timeValue > 0) {
            const step = calculateStepTime(timeValue, selectedTime);
            const dataArray = [];

            if (selectedTime === ChronoUnit.MONTHS) {
              while (dateFromForMonths.getTime() <= calculateTo) {
                dataArray.push({ key: `${dateFromForMonths.toISOString()}`, value: newValue });
                dateFromForMonths.setMonth(dateFromForMonths.getMonth() + step);
              }
            } else {
              while (calculateFrom <= calculateTo) {
                dataArray.push({
                  key: `${new Date(calculateFrom).toISOString()}`,
                  value: newValue,
                });
                calculateFrom += step;
              }
            }

            const originalItem = originalDataQuery.find(orig => orig.identifier === identifier);
            hasChanged = dataArray.some(({ key, value }) => {
              const originalEntry = originalItem?.data.find(entry => entry.key === key);
              return !originalEntry || originalEntry.value !== value;
            });

            return {
              ...item,
              data: dataArray,
            };
          }
        } else {
          const originalItem = originalDataQuery.find(orig => orig.identifier === identifier);
          const originalValue =
            originalItem && originalItem.data && originalItem.data.length > 0
              ? originalItem.data[0].value
              : undefined;

          if (originalValue !== newValue) {
            hasChanged = true;
          }

          return {
            ...item,
            data:
              item.data.length > 0
                ? [{ ...item.data[0], value: newValue }]
                : [{ key: `${dateTo.toISOString()}`, value: newValue }],
          };
        }
      }
      return item;
    });

    setDataQuery(tempArr);
    setIsValuesUpdated(hasChanged);
  };

  useEffect(() => {
    if (!rangeSelected) {
      setValueCount(1);
      return;
    }
    let calculateFrom = dateFrom.getTime();
    const calculateTo = dateTo.getTime();
    if (timeValue > 0) {
      const step = calculateStepTime(timeValue, selectedTime);
      let count = 0;
      if (selectedTime === ChronoUnit.MONTHS) {
        const monthsDiff =
          (dateTo.getFullYear() - dateFrom.getFullYear()) * 12 +
          dateTo.getMonth() -
          dateFrom.getMonth();
        if (dateTo.getDate() < dateFrom.getDate()) {
          count = monthsDiff - 1;
        }
        count = monthsDiff;
      } else {
        while (calculateFrom <= calculateTo) {
          calculateFrom += step;
          count++;
        }
      }
      setValueCount(count);
    }
  }, [timeValue, selectedTime, rangeSelected]);

  const dateFromHandler = (e: Date) => {
    setDateFrom(e);
    sessionStorage.setItem("dateFrom", e.toISOString());
  };

  const dateToHandler = (e: Date) => {
    setDateTo(e);
    sessionStorage.setItem("dateTo", e.toISOString());
  };

  return (
    <>
      {fetchQuery.isError && <>{T("alert.error")}...</>}

      {!fetchQuery.isError && (
        <div className="flex flex-column gap-3 mt-2">
          <div className="flex gap-3 align-items-center">
            <p className="mb-0">{T("word.range")}</p>
            <InputSwitch checked={rangeSelected} onChange={e => setRangeSelected(e.value)} />
          </div>

          <div>
            {rangeSelected && (
              <CalendarWrapper
                id="dateFrom"
                showSeconds
                value={dateFrom}
                onChange={dateFromHandler}
                dateFrequency={dateFrequency}
              />
            )}
            <CalendarWrapper
              id="dateTo"
              showSeconds
              value={dateTo}
              onChange={dateToHandler}
              dateFrequency={dateFrequency}
            />
          </div>
          {rangeSelected && (
            <div className="flex gap-2 align-items-center">
              <p className="mb-0">{T("word.every")}: </p>
              <InputNumber
                className="max-w-max"
                value={timeValue}
                onValueChange={e => setTimeValue(e.value as number)}
              />
              <TimeUnitDropdown
                value={selectedTime}
                onChange={e => setSelectedTime(e.value)}
                disabledValue={ChronoUnit.YEARS}
              />
              <div>
                {T("message.expected-values-count")}: {valueCount}
              </div>
            </div>
          )}

          <DataTable
            value={dataQuery}
            editMode="cell"
            className="editable-cells-table"
            responsiveLayout="scroll"
            scrollHeight="50vh"
            loading={fetchQuery.isLoading}
            size="small"
          >
            <Column
              field="title"
              header={T("word.dataset")}
              body={titleColumnRender}
              style={{ width: "90%" }}
            />
            <Column field="unit" header={T("word.value")} body={valueBodyInputTemplate} />
          </DataTable>
          <div className="button-container flex gap-2">
            <Button
              label={T("action.update")}
              icon="pi pi-pencil"
              onClick={updateHandler}
              disabled={!isValuesUpdated}
              className="p-button-success p-button-rounded p-button-sm"
            />
            <Button
              label={T("word.cancel")}
              icon="pi pi-times"
              onClick={() => closeDialog()}
              className="p-button-danger p-button-rounded p-button-sm"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default AddDataDialog;
